/**
 * ProviderOSDashboard — aggregated provider workspace.
 *
 * Single-screen "morning view" for a provider org. Reads from:
 *   GET /api/provider/dashboard — counts + next payout + upcoming visits
 * All gated server-side by the `provider_os` entitlement.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Activity, CalendarClock, DollarSign, FileText, Inbox,
  AlertTriangle, CheckCircle2, Clock, ArrowRight, Loader2, Wallet,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProviderOSGate from '../components/provider_os/ProviderOSGate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatUsd = (cents) => {
  if (!cents && cents !== 0) return '$0.00';
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const KpiTile = ({ icon: Icon, label, value, sub, tone = 'slate', testId }) => {
  const toneMap = {
    slate:   'bg-slate-50 border-slate-200 text-slate-900',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    amber:   'bg-amber-50 border-amber-200 text-amber-900',
    blue:    'bg-blue-50 border-blue-200 text-blue-900',
    red:     'bg-red-50 border-red-200 text-red-900',
  };
  return (
    <div className={`rounded-2xl border p-5 ${toneMap[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-70">
        <Icon className="w-4 h-4" />
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold">{value}</div>
      {sub && <div className="mt-1 text-xs opacity-70">{sub}</div>}
    </div>
  );
};

const StatusChip = ({ label, count, tone = 'slate' }) => {
  const toneMap = {
    slate:   'bg-slate-100 text-slate-800',
    emerald: 'bg-emerald-100 text-emerald-800',
    amber:   'bg-amber-100 text-amber-800',
    blue:    'bg-blue-100 text-blue-800',
    red:     'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${toneMap[tone]}`}>
      <span>{label}</span>
      <span className="font-bold">{count}</span>
    </span>
  );
};

export default function ProviderOSDashboard() {
  return (
    <ProviderOSGate>
      <DashboardInner />
    </ProviderOSGate>
  );
}

function DashboardInner() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/provider/dashboard`,
        { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading provider dashboard…
      </div>
    );
  }

  const wi = data.work_items;
  const fv = data.first_visit;
  const pw = data.paperwork;
  const pay = data.payouts;

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-8" data-testid="provider-os-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-slate-900">Provider OS</h1>
        <p className="text-sm text-slate-500 mt-1">
          Accept referrals, sign orders, commit first visits — in that order.
        </p>
      </div>

      {/* KPI row — ordered by the loop's gates: ① accept ② sign ③ commit ④ watch.
          Payouts trail last, display-only. */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KpiTile
          icon={Inbox}
          label="Offers awaiting acceptance"
          value={(data.offers || {}).awaiting_count || 0}
          sub="Human gate #1 — first to accept wins"
          tone={(data.offers || {}).awaiting_count > 0 ? 'amber' : 'slate'}
          testId="kpi-offers-awaiting"
        />
        <KpiTile
          icon={FileText}
          label="Paperwork awaiting signature"
          value={(pw.by_status.draft || 0) + (pw.by_status.out_for_sig || 0)}
          sub="Human gate #2 — sign in-platform"
          tone={((pw.by_status.draft || 0) + (pw.by_status.out_for_sig || 0)) > 0 ? 'blue' : 'slate'}
          testId="kpi-paperwork"
        />
        <KpiTile
          icon={CalendarClock}
          label="First visits to commit"
          value={fv.today_count}
          sub={`${fv.overdue_count} overdue`}
          tone={fv.overdue_count > 0 ? 'red' : 'emerald'}
          testId="kpi-visits-today"
        />
        <KpiTile
          icon={Activity}
          label="Active work"
          value={wi.total}
          sub={`${wi.by_status.opened || 0} opened · ${wi.by_status.first_visit_locked || 0} locked`}
          tone="slate"
          testId="kpi-work-items"
        />
        <KpiTile
          icon={DollarSign}
          label="Pending payouts"
          value={formatUsd(pay.total_pending_cents)}
          sub={`${formatUsd(pay.total_paid_cents)} paid lifetime · display only`}
          tone="slate"
          testId="kpi-payouts-pending"
        />
      </div>

      {/* One-click gate actions — both human gates reachable from the top */}
      <div className="flex gap-3 flex-wrap" data-testid="provider-gate-actions">
        <Link to="/provider-os/work"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
          data-testid="provider-accept-cta">
          <Inbox className="w-4 h-4" /> Review offers & work
        </Link>
        <Link to="/provider-os/documents"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-slate-300 text-slate-800 text-sm font-medium hover:bg-slate-50"
          data-testid="provider-sign-cta">
          <FileText className="w-4 h-4" /> Sign paperwork
        </Link>
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upcoming first-visits */}
        <div className="lg:col-span-2 rounded-2xl border border-slate-200 p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-slate-700" />
              <h2 className="text-lg font-semibold text-slate-900">Upcoming first visits</h2>
            </div>
            <Link to="/provider-os/work" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
              All work <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {fv.upcoming.length === 0 ? (
            <p className="text-sm text-slate-500">No upcoming first-visit windows.</p>
          ) : (
            <ul className="space-y-2">
              {fv.upcoming.slice(0, 8).map((v) => (
                <li key={v.placement_id} className="flex items-center justify-between text-sm py-2 border-b border-slate-100 last:border-0">
                  <span className="font-mono text-xs text-slate-500">{v.placement_id.slice(0, 8)}…</span>
                  <span className="text-slate-700">{new Date(v.first_visit_at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Payouts panel */}
        <div className="rounded-2xl border border-slate-200 p-6 bg-white">
          <div className="flex items-center gap-2 mb-4">
            <Wallet className="w-5 h-5 text-slate-700" />
            <h2 className="text-lg font-semibold text-slate-900">Earnings ledger</h2>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <StatusChip label="Pending" count={pay.by_status.pending || 0} tone="slate" />
            <StatusChip label="Ready" count={pay.by_status.ready || 0} tone="amber" />
            <StatusChip label="Paid" count={pay.by_status.paid || 0} tone="emerald" />
          </div>
          {pay.next_payout ? (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4" data-testid="next-payout-card">
              <div className="text-xs uppercase text-emerald-700 mb-1">Next payout</div>
              <div className="text-2xl font-semibold text-emerald-900">
                {formatUsd(pay.next_payout.net_cents)}
              </div>
              <div className="text-xs text-emerald-700/80 mt-1">
                {pay.next_payout.fast_pay ? 'Fast-pay enabled · ' : 'Standard · '}
                placement {pay.next_payout.placement_id.slice(0, 8)}…
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500">No ready payouts yet.</p>
          )}
          <div className="mt-4">
            <Link to="/provider-os/payouts" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
              Full ledger <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Paperwork pulse */}
      <div className="rounded-2xl border border-slate-200 p-6 bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-slate-700" />
            <h2 className="text-lg font-semibold text-slate-900">Paperwork pulse</h2>
          </div>
          <Link to="/provider-os/documents" className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
            All documents <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusChip label="Draft" count={pw.by_status.draft || 0} tone="slate" />
          <StatusChip label="Out for sig" count={pw.by_status.out_for_sig || 0} tone="blue" />
          <StatusChip label="Signed" count={pw.by_status.signed || 0} tone="emerald" />
          <StatusChip label="Exchanged" count={pw.by_status.exchanged || 0} tone="emerald" />
          <StatusChip label="Cancelled" count={pw.by_status.cancelled || 0} tone="red" />
        </div>
      </div>

      <p className="text-xs text-slate-400 text-center">
        Generated at {new Date(data.generated_at).toLocaleString()}
      </p>
    </div>
  );
}
